/**
 * Banner ads — fixed-bottom AdMob (native) / AdSense (web) banner shown on
 * the Lobby and other passive screens. Hidden during gameplay.
 *
 * Web monetization activates when both env vars are set:
 *   REACT_APP_ADSENSE_CLIENT (e.g. "ca-pub-XXXXXXXXXXXXXXXX")
 *   REACT_APP_ADSENSE_BANNER_SLOT (numeric slot id)
 * Without them, the polite stub is rendered so layout still works in dev.
 *
 * Native: Capacitor AdMob plugin (BannerAdSize.ADAPTIVE_BANNER, anchored
 * BOTTOM_CENTER).
 */

let _mounted = false;
let _adsenseScriptLoaded = false;

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || "";
const ADSENSE_SLOT = process.env.REACT_APP_ADSENSE_BANNER_SLOT || "";

// Real AdSense should ONLY fire in production builds on the live domain.
// Clicks/impressions from dev/preview risk getting the publisher flagged for
// "invalid traffic" under AdSense policy — Google can suspend the account.
// Dev/preview always falls back to the stub (which keeps the "Remove ads"
// conversion UX intact without serving real ads).
const IS_PROD_BUILD = process.env.NODE_ENV === "production";
const ADSENSE_ENABLED = IS_PROD_BUILD && !!ADSENSE_CLIENT && !!ADSENSE_SLOT;

const isCapacitorNative = () => {
  try {
    return typeof window !== "undefined"
      && window.Capacitor
      && window.Capacitor.isNativePlatform
      && window.Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

// Reserve enough vertical space at the bottom of the viewport so the banner
// never overlaps content. ~60px banner + 8px safe-area padding. On native
// (where we don't render any banner) reserve 0px so the lobby uses the full
// screen. Detect via hostname (avoid Capacitor bridge timing race).
export const BANNER_HEIGHT_PX = (typeof window !== "undefined"
  && window.location
  && (window.location.hostname === "localhost"
      || window.location.hostname === "127.0.0.1"))
  ? 0
  : 68;

function ensureAdsenseScript() {
  if (_adsenseScriptLoaded) return;
  if (!ADSENSE_ENABLED) return;
  // The pagead script is idempotent — adding it twice is harmless, but we
  // gate it anyway to avoid the console warning.
  if (document.querySelector('script[data-quattro-adsense]')) {
    _adsenseScriptLoaded = true;
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.dataset.quattroAdsense = "1";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(ADSENSE_CLIENT)}`;
  document.head.appendChild(s);
  _adsenseScriptLoaded = true;
}

function buildRemoveAdsLink() {
  const link = document.createElement("a");
  link.href = "/store?highlight=ad_free";
  link.setAttribute("data-testid", "banner-remove-ads-link");
  link.setAttribute("aria-label", "Remove ads — 30-day pass");
  // NOTE: position is FIXED to the viewport (not absolute to the banner) and
  // the z-index is set higher than any app modal backdrop. Modals use
  // z-index 80–120; we park at 200 so the pill is always clickable no matter
  // what is open on screen. The banner itself stays at z-40 (so modals still
  // cover it cleanly). Mirrors the banner height so the pill rides right
  // above the ad strip visually.
  Object.assign(link.style, {
    position: "fixed",
    bottom: `${BANNER_HEIGHT_PX - 6}px`,
    right: "10px",
    zIndex: "200",
    padding: "4px 11px",
    borderRadius: "999px",
    fontFamily: "system-ui, sans-serif",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: "#FFD700",
    background: "rgba(11,11,11,0.85)",
    border: "1px solid rgba(255,215,0,0.55)",
    textDecoration: "none",
    lineHeight: "1.4",
    pointerEvents: "auto",
    whiteSpace: "nowrap",
    backdropFilter: "blur(4px)",
    boxShadow: "0 2px 12px rgba(255,215,0,0.25)",
  });
  link.innerHTML = `Remove ads →`;
  // Keep the explicit JS navigation so clicks bypass any stray intercept.
  link.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.location.assign(link.href);
  });
  return link;
}

function ensureRemoveAdsLink() {
  // The remove-ads pill lives as a SEPARATE fixed element (sibling, not child
  // of the banner) so its high z-index can float above modal backdrops even
  // when the banner itself sits underneath them.
  if (typeof document === "undefined") return null;
  const existing = document.getElementById("quattro-remove-ads-link");
  if (existing) return existing;
  const link = buildRemoveAdsLink();
  link.id = "quattro-remove-ads-link";
  document.body.appendChild(link);
  return link;
}

function ensureWebElement() {
  if (typeof document === "undefined") return null;
  let el = document.getElementById("quattro-banner-stub");
  if (el) return el;
  el = document.createElement("div");
  el.id = "quattro-banner-stub";
  Object.assign(el.style, {
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    height: `${BANNER_HEIGHT_PX}px`,
    zIndex: "40",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(180deg, rgba(11,11,11,0.85), rgba(11,11,11,0.98))",
    borderTop: "1px solid rgba(255,255,255,0.08)",
    backdropFilter: "blur(12px)",
    overflow: "hidden",
    pointerEvents: "auto",
  });

  // Real AdSense unit — production builds only, when env vars are set.
  if (ADSENSE_ENABLED) {
    el.setAttribute("data-testid", "banner-ad-adsense");
    const ins = document.createElement("ins");
    ins.className = "adsbygoogle";
    Object.assign(ins.style, {
      display: "block",
      width: "100%",
      height: `${BANNER_HEIGHT_PX}px`,
    });
    ins.setAttribute("data-ad-client", ADSENSE_CLIENT);
    ins.setAttribute("data-ad-slot", ADSENSE_SLOT);
    ins.setAttribute("data-ad-format", "auto");
    ins.setAttribute("data-full-width-responsive", "true");
    el.appendChild(ins);
    document.body.appendChild(el);
    ensureRemoveAdsLink();
    ensureAdsenseScript();
    try {
      if (!ins.getAttribute("data-adsbygoogle-status")) {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.warn("adsbygoogle push failed:", e);
    }
    return el;
  }

  // Dev/preview stub — informational placeholder only.
  el.setAttribute("data-testid", "banner-ad-stub");
  Object.assign(el.style, {
    color: "rgba(255,255,255,0.55)",
    fontFamily: "system-ui, sans-serif",
    fontSize: "11px",
    letterSpacing: "2px",
    textTransform: "uppercase",
  });
  el.innerHTML = `
    <span style="color:#3DDC97;margin-right:10px;">SPONSORED</span>
    <span>Your banner ad will appear here</span>
  `;
  document.body.appendChild(el);
  ensureRemoveAdsLink();
  return el;
}

function removeWebElement() {
  if (typeof document === "undefined") return;
  const el = document.getElementById("quattro-banner-stub");
  if (el && el.parentNode) el.parentNode.removeChild(el);
  const link = document.getElementById("quattro-remove-ads-link");
  if (link && link.parentNode) link.parentNode.removeChild(link);
}

/** Idempotent: safe to call from concurrent components. */
export async function mountBanner() {
  if (_mounted) return;
  _mounted = true;

  if (isCapacitorNative()) {
    // ❌ Banner ads DISABLED on Android — design decision: banners hurt
    // retention in card games. Monetization on native is via interstitials
    // (every 4 games) + rewarded videos (Watch Ad +50/+200, rescue, etc.)
    // + Play Store IAP (gems). The web keeps AdSense banner for traffic
    // monetization where users expect them.
    return;
  }
  ensureWebElement();
}

export async function unmountBanner() {
  if (!_mounted) return;
  _mounted = false;

  if (isCapacitorNative()) {
    try {
      const { AdMob } = await import("@capacitor-community/admob");
      await AdMob.removeBanner();
    } catch (e) {
      console.warn("banner: native removeBanner failed:", e);
    }
    return;
  }
  removeWebElement();
}

/** Reads from localStorage so the future "Remove Ads" IAP can flip it.
 *  The localStorage key is set as an ISO timestamp (ad-free expiry); we
 *  check it's still in the future. Empty/null/past = ads ON. */
export function shouldShowBanner() {
  try {
    const raw = localStorage.getItem("quattro_remove_ads");
    if (!raw) return true;
    if (raw === "1") return false; // legacy "forever" flag — honor it
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return true;
    return d.getTime() <= Date.now();
  } catch {
    return true;
  }
}

/** Mark this device as ad-free until the given expiry (ISO string).
 *  Idempotent. Also unmounts any currently-shown banner if active. */
export function markAdsRemovedUntil(isoExpiry) {
  if (!isoExpiry) return;
  try { localStorage.setItem("quattro_remove_ads", String(isoExpiry)); } catch {}
  // Tear down the currently-mounted banner immediately if expiry is future.
  try {
    const d = new Date(isoExpiry);
    if (!Number.isNaN(d.getTime()) && d.getTime() > Date.now()) {
      unmountBanner();
    }
  } catch {}
}

/** Sync the server-side flag (`profile.ads_removed_until`) into localStorage
 *  so cross-device users (re-installs, multiple devices on same Google account)
 *  retain the benefit. Call this after every profile fetch. */
export function syncAdsRemovedFromProfile(profile) {
  if (!profile) return;
  if (profile.ads_removed_until) {
    try { localStorage.setItem("quattro_remove_ads", String(profile.ads_removed_until)); } catch {}
  }
}

/** Convenience: returns true iff the profile's pass is currently active. */
export function isAdsRemovedActive(profile) {
  if (!profile?.ads_removed_until) return false;
  const d = new Date(profile.ads_removed_until);
  if (Number.isNaN(d.getTime())) return false;
  return d.getTime() > Date.now();
}
